import { useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Grid,
  ReactSelectController,
} from "@pushpress/shared-components";
import MemberProfileSelect from "../../../components/controllers/MemberProfileSelect";
import { useFeatureFlag } from "../../../context/features";
import { RECURRING_APPT_FEATURE_FLAG } from "../../../context/features/features.utils";
import { joinStrings } from "../../../utils/misc";
import {
  AppointmentTypeFragment,
  StaffFragment,
} from "./AppointmentsStart-generated.hooks";
import { statusOptions } from "./AppointmentsStart.utils";
import { AppointmentFilter } from "../Appointments.types";

interface AppointmentsFilterProps {
  staff: StaffFragment[];
  appointmentTypes: AppointmentTypeFragment[];
  filter?: AppointmentFilter;
  hideStaff?: boolean;
  onFilterChange?: (filter: AppointmentFilter) => void;
  onClear?: () => void;
}

function AppointmentsFilter(props: AppointmentsFilterProps) {
  const {
    filter = {},
    staff,
    appointmentTypes,
    hideStaff,
    onFilterChange,
    onClear,
  } = props;

  const { isFeatureEnabled } = useFeatureFlag();

  const recurringBookingFlag = isFeatureEnabled(RECURRING_APPT_FEATURE_FLAG);

  const { control, handleSubmit } = useForm<AppointmentFilter>({
    defaultValues: filter,
  });

  const staffOptions = useMemo(() => {
    return [...staff]
      .filter((item) => !!item)
      .sort((a, b) => {
        if (!a?.firstName || !b?.firstName) {
          return 0;
        }
        return a.firstName?.localeCompare(b?.firstName);
      });
  }, [staff]);

  const appointmentTypesOptions = useMemo(() => {
    return [...appointmentTypes].sort((a, b) => {
      if (!a?.name || !b?.name) {
        return 0;
      }
      return a.name?.localeCompare(b?.name);
    });
  }, [appointmentTypes]);

  return (
    <form
      className="p-4"
      style={{ width: 320 }}
      onSubmit={handleSubmit(onFilterChange)}
    >
      <MemberProfileSelect
        label="Member"
        control={control}
        name="member"
        isClearable
        placeholder="Search for a member or lead"
      />
      <ReactSelectController
        label="Appointment type"
        control={control}
        name="appointmentTypes"
        isMulti
        isClearable
        hideDropdownIndicator
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        options={appointmentTypesOptions}
        getOptionValue={(at) => at.uuid}
        getOptionLabel={(at) => at.name}
        getOptionColor={(at) => at.color}
      />
      {!hideStaff && (
        <ReactSelectController
          label="Staff"
          control={control}
          name="staff"
          isClearable
          isMulti
          options={staffOptions}
          hideDropdownIndicator
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          getOptionValue={(staff) => staff.userUuid}
          getOptionLabel={(staff) =>
            joinStrings(staff.firstName, staff.lastName)
          }
        />
      )}
      {recurringBookingFlag && (
        <ReactSelectController
          label="Status"
          control={control}
          name="status"
          isClearable
          isMulti
          hideDropdownIndicator
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          options={statusOptions}
          getOptionValue={(option) => option.label}
        />
      )}
      <Grid container justifyContent="flex-end" className="mt-2">
        <Button
          text="Clear"
          buttonType="secondary"
          onClick={onClear}
          type="button"
        />
        <Button
          text="Apply"
          buttonType="primary"
          className="ml-2"
          type="submit"
        />
      </Grid>
    </form>
  );
}

export default AppointmentsFilter;
